if (!customElements.get('smart-carousel')) {
  class SmartCarousel extends HTMLElement {
    constructor() {
      super();
      this.mode = this.dataset.mode;
      this.storageKey = 'recentlyViewedProducts';
      this.limit = parseInt(this.dataset.limit) || 8;
      // Get currency format from Liquid or fallback to generic
      this.moneyFormat = this.dataset.moneyFormat || window.Shopify.money_format || '${{amount}}';
      this.currency = this.dataset.currency;
      this.currencySymbol = this.dataset.currencySymbol;
      this.root = window.Shopify?.routes?.root || '/';
      this.cleanRoot = this.root.endsWith('/') ? this.root : this.root + '/';
      this.activeCollectionHandle = document.documentElement.dataset.collection;
      this.carousel = this.querySelector('.rv-carousel');
      this.prevBtn = this.querySelector('.carousel-btn.prev');
      this.nextBtn = this.querySelector('.carousel-btn.next');
      try {
        this.i18n = JSON.parse(this.dataset.i18n || '{}');
      } catch (e) {
        console.error("I18N Bridge Error:", e);
        this.i18n = { sale: 'Sale', sold_out: 'Sold Out', from_html: 'From [price]' };
      }
    }

    optimizeShopifyUrl(url, size) {
      if (!url || typeof url !== 'string') return url;

      // 1. Strip out any legacy size indicators (e.g., _360x360, _100x, _master) from the base filename
      let cleanUrl = url.replace('//cdn.shopify.com/s/files/1/0887/2001/5708', '//www.happycandles.ro/cdn/shop');
      cleanUrl = cleanUrl.replace(/_(?:[0-9]+x[0-9]*|[0-9]*x[0-9]+|master)/g, '');

      // 2. Extract just the numeric width value if a string like "360x360" or "360x" was passed
      const widthMatch = String(size).match(/^([0-9]+)/);
      const widthValue = widthMatch ? widthMatch[1] : size;

      // 3. Separate the base path from the query string
      const parts = cleanUrl.split('?');
      const basePath = parts[0];
      const queryString = parts[1];

      if (queryString) {
        // If query params exist (like ?v=1776195392), parse them safely
        const params = new URLSearchParams(queryString);
        params.set('width', widthValue);
        return `${basePath}?${params.toString()}`;
      } else {
        // If no query parameters existed originally, create a fresh one
        return `${basePath}?width=${widthValue}`;
      }
    }    

    connectedCallback() {
      const priorityMap = {
        'complementary': 0,
        'related': 150,
        'history': 400
      };

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          const delay = priorityMap[this.mode] || 0;
          setTimeout(() => {
            this.init();
          }, delay);
          observer.disconnect();
        }
      }, { 
        rootMargin: '300px 0px 300px 0px' 
      });
      observer.observe(this);
    }

    init() {
      this.render();
    }

    async render() {
      const engineType = this.mode;
      const productId = this.dataset.productId;
      const carouselSize = this.limit;
      const carouselCols = parseInt(this.dataset.columns) || 4;
      const isMobile = window.innerWidth < 750;
      const pathParts = window.location.pathname.match(/\/collections\/([^\/]+)/);
      const currentSpirit = pathParts ? pathParts[1] : null;      
      
      // SKELETON RENDER
      const skeletonCount = isMobile ? 2 : carouselCols;
      const skeletonHTML = Array(skeletonCount).fill(`
        <div class="carousel-product-card skeleton-card">
          <div class="skeleton-image-wrapper">
            <div class="shimmer-box" style="width: 100%; aspect-ratio: 1/1; background: #eee;"></div>
          </div>
          <div class="product-card-info">
            <div class="shimmer-box" style="height: 14px; background: #eee; width: 80%; margin-top: 15px;"></div>
            <div class="shimmer-box" style="height: 10px; background: #eee; width: 60%; margin-top: 10px;"></div>
            <div class="shimmer-box" style="height: 14px; background: #eee; width: 30%; margin-top: auto; padding-top: 20px;"></div>
          </div>
        </div>
      `).join('');
      this.carousel.innerHTML = skeletonHTML;

      // IMAGE SIZE LOGIC
      let imgSize = '640x640';
      if (isMobile) {
        imgSize = '480x480'; 
      } else {
        if (carouselCols >= 7) imgSize = '240x240';
        else if (carouselCols >= 5) imgSize = '360x360';
        else if (carouselCols >= 3) imgSize = '480x480';
      }

      try {
        let finalProducts = [];

        if (engineType === 'history') {
          let paths = [];
          try {
            paths = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
          } catch(e) {}
          
          const cleanHistory = (path) => {
            try {
              let pathsToClean = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
              pathsToClean = pathsToClean.filter(p => p !== path);
              localStorage.setItem(this.storageKey, JSON.stringify(pathsToClean));
            } catch (e) {}
          };
          
          // 1. ROBUST EXCLUSION: Added safety check to prevent crash on non-product pages
          const currentPath = window.location.pathname;
          const currentHandle = currentPath.includes('/products/') 
            ? currentPath.split('/products/')[1].split('?')[0].replace(/\/$/, "") 
            : null;

          paths = paths.filter(p => {
            if (!p) return false;
            const pHandle = p.includes('/products/') 
              ? p.split('/products/')[1].split('?')[0].replace(/\/$/, "") 
              : p;
            
            return pHandle !== currentHandle;
          });

          // 2. FETCH & MAP
          finalProducts = (await Promise.all(paths.map(async (path) => {
            try {
              const handle = path.includes('/products/') 
                              ? path.split('/products/')[1].split('?')[0].replace(/\/$/, "") 
                              : path;
              
              const res = await fetch(`${window.location.origin}${this.cleanRoot}products/${handle}.js`);
              
              if (!res.ok) {
                  cleanHistory(path); // Self-clean the 404 entry
                  return null; 
              }
              
              let data = await res.json();
              // CRITICAL: Overwrite data.url with the STORED path (to keep the spirit params)
              const localePrefix = new RegExp('^/[a-z]{2}(-[a-z]{2})?/');
              const normalizedPath = path.replace(localePrefix, '/');
              data.url = `${this.cleanRoot}${normalizedPath.slice(1)}`;
              return data;
            } catch (err) {
              // Catch network timeouts or malformed JSON to prevent engine freeze
              return null;
            }
          }))).filter(p => p && p.available).slice(0, this.limit);
        }
        else if (engineType === 'related') {
          const [localRes, globalRes] = await Promise.all([
              fetch(`${this.cleanRoot}recommendations/products.json?product_id=${productId}&intent=${engineType}&limit=${carouselSize * 5}`),
              fetch(`${this.cleanRoot}collections/zenith-special-offers/products.json?limit=10`)
          ]);
          const localData = await localRes.json();
          const globalData = await globalRes.json();

          const getPrice = (val) => {
            if (!val) return 0;
            if (typeof val === 'string' && val.includes('.')) return parseFloat(val) * 100;
            return parseFloat(val);
          };

          const normalize = (p) => {
            if (p.variants && p.variants.length > 0) {
                const v = p.variants[0];
                p.price = getPrice(p.price) || getPrice(v.price);
                p.compare_at_price = getPrice(p.compare_at_price) || getPrice(v.compare_at_price);
                if (p.available === undefined) p.available = v.available;
            }
            if (!p.featured_image) {
              if (p.images && p.images[0]) {
                 p.featured_image = (typeof p.images[0] === 'string') ? p.images[0] : (p.images[0].src || '');
              } else {
                 p.featured_image = '';
              }
            }
            if (!p.images) p.images = [];
            if (!p.images[1]) {
               p.images[1] = p.featured_image;
            } else {
               p.images[1] = (typeof p.images[1] === 'string') ? p.images[1] : (p.images[1].src || p.featured_image);
            }
            return p;
          };

          const rawRelated = (localData.products || []).map(normalize);
          const rawGlobalSale = (globalData.products || []).map(normalize);

          let relatedSale = rawRelated.filter(p => (getPrice(p.compare_at_price) > getPrice(p.price) && p.available && p.id != productId));
          let relatedRegular = rawRelated.filter(p => ((!p.compare_at_price || !(getPrice(p.compare_at_price) > getPrice(p.price))) && p.available && p.id != productId));
          let globalSale = rawGlobalSale.filter(p => p.available && p.id != productId);

          if (relatedRegular.length < carouselSize) {
              const collectionHandle = this.dataset.collectionHandle || 'all';
              var backup = await (await fetch(`${window.location.origin}${this.cleanRoot}collections/${collectionHandle}/products.json?limit=${carouselSize * 2}`)).json();
              var globalRegular = (backup.products || []).map(normalize).filter(p => 
                  p.available && !(getPrice(p.compare_at_price) > getPrice(p.price)) && p.id != productId
              );
          }

          let selection = [];
          let slots = carouselSize;

          if (globalSale.length > 0) {
              selection.push(globalSale[Math.floor(Math.random() * globalSale.length)]);
              slots--;
          }
          relatedSale = relatedSale.filter(ls => !selection.find(fs => fs.id === ls.id));
          if (relatedSale.length > 0 && slots > 0) {
              selection.push(relatedSale.splice(Math.floor(Math.random() * relatedSale.length), 1)[0]);
              slots--;
          }
          while (slots > 0 && relatedRegular.length > 0) {
              selection.push(relatedRegular.splice(Math.floor(Math.random() * relatedRegular.length), 1)[0]);
              slots--;
          }
          if (slots > 0) {
            while (slots > 0 && relatedSale.length > 0) {
              selection.push(relatedSale.splice(Math.floor(Math.random() * relatedSale.length), 1)[0]);
              slots--;
            }
            if (slots > 0 && typeof globalRegular !== 'undefined'){
              globalRegular = globalRegular.filter(ls => !selection.find(fs => fs.id === ls.id));
              while (slots > 0 && globalRegular.length > 0) {
                selection.push(globalRegular.splice(Math.floor(Math.random() * globalRegular.length), 1)[0]);
                slots--;
              }
            }
          }
          finalProducts = selection;
        } 
        else if (engineType === 'complementary'){
          const response = await fetch(`${this.cleanRoot}recommendations/products.json?product_id=${productId}&intent=${engineType}&limit=${carouselSize}`);
          const localData = await response.json();

          const getPrice = (val) => {
              if (!val) return 0;
              if (typeof val === 'string' && val.includes('.')) return parseFloat(val) * 100;
              return parseFloat(val);
          };

          const normalize = (p) => {
            if (p.variants && p.variants.length > 0) {
                const v = p.variants[0];
                p.price = getPrice(p.price) || getPrice(v.price);
                p.compare_at_price = getPrice(p.compare_at_price) || getPrice(v.compare_at_price);
                if (p.available === undefined) p.available = v.available;
            }
            if (!p.featured_image) {
                if (p.images && p.images[0]) {
                   p.featured_image = (typeof p.images[0] === 'string') ? p.images[0] : (p.images[0].src || '');
                } else { p.featured_image = ''; }
            }
            if (!p.images) p.images = [];
            if (!p.images[1]) {
                p.images[1] = p.featured_image;
            } else {
                p.images[1] = (typeof p.images[1] === 'string') ? p.images[1] : (p.images[1].src || p.featured_image);
            }
            return p;
          };

          const rawComplementary = (localData.products || []).map(normalize);
          let compSale = rawComplementary.filter(p => (getPrice(p.compare_at_price) > getPrice(p.price) && p.available && p.id != productId));
          let compRegular = rawComplementary.filter(p => (!p.compare_at_price || !(getPrice(p.compare_at_price) > getPrice(p.price))) && p.available && p.id != productId);

          let selection = [];
          let slots = carouselSize;

          if (compSale.length > 0) {
              selection.push(compSale.splice(Math.floor(Math.random() * compSale.length), 1)[0]);
              slots--;
          }
          while (slots > 0 && compRegular.length > 0) {
              selection.push(compRegular.splice(Math.floor(Math.random() * compRegular.length), 1)[0]);
              slots--;
          }
          while (slots > 0 && compSale.length > 0) {
              selection.push(compSale.splice(Math.floor(Math.random() * compSale.length), 1)[0]);
              slots--;
          }
          finalProducts = selection;
        }
        
        if (finalProducts.length > 0) {
          if (engineType !== 'history') finalProducts.sort(() => Math.random() - 0.5);

          this.carousel.innerHTML = finalProducts.map(p => {
            if (engineType !== 'history') {
              const tags = typeof p.tags === 'string'
                ? p.tags.split(',').map(t => t.trim().toLowerCase())
                : (p.tags || []).map(t => t.toLowerCase());

              const collectionTags = tags.filter(t => !t.startsWith('b:'));

              const collectionHandle = collectionTags.includes(this.activeCollectionHandle)
                ? this.activeCollectionHandle
                : tags.find(t => t.startsWith('b:'))?.split(':')[1] ?? collectionTags[0];

              p.url = `${this.cleanRoot}collections/${collectionHandle}/products/${p.handle}`;
              p.collection = collectionHandle;
            } else {
              const match = p.url?.match(/\/collections\/([^\/]+)/);
              p.collection = match ? match[1] : this.activeCollectionHandle;
            }

            return this.cardTemplate(p, imgSize, isMobile);
          }).join('');

          if (finalProducts.length > carouselCols) this.initNav();
        } else {
          this.style.display = 'none';
        }
      } catch (e) { console.error("Critical Engine Error:", e); }
    }

    cardTemplate(p, targetSize, isMobile) {
      // MASTER SALE LOGIC: Check all variants
      let isOnSale = p.compare_at_price > p.price;
      if (!isOnSale && p.variants) {
        isOnSale = p.variants.some(v => v.compare_at_price > v.price);
      }
      
      const isSoldOut = !p.available;
      const getUrl = (img) => (img && typeof img === 'object' ? img.src : img) || '';

      // 1. Primary Image Logic
      const mainImgBase = getUrl(p.featured_image);
      const mainImg = this.optimizeShopifyUrl(mainImgBase, targetSize);
      const mainImgSrcset = `
        ${this.optimizeShopifyUrl(mainImgBase, '180')} 180w, 
        ${this.optimizeShopifyUrl(mainImgBase, '240')} 240w, 
        ${this.optimizeShopifyUrl(mainImgBase, '360')} 360w, 
        ${this.optimizeShopifyUrl(mainImgBase, '480')} 480w, 
        ${this.optimizeShopifyUrl(mainImgBase, '640')} 640w
      `.trim();

      // 2. Hover Image Logic
      const cardSize = this.dataset.columns < 3 ? "large" : this.dataset.columns < 6 ? "medium" : "small";
      const hoverImgRaw = (p.images && p.images[1]) ? p.images[1] : p.featured_image;
      const hoverImgBase = getUrl(hoverImgRaw);
      const hoverImg = this.optimizeShopifyUrl(hoverImgBase, targetSize);
      const hoverImgSrcset = `
        ${this.optimizeShopifyUrl(hoverImgBase, '180')} 180w, 
        ${this.optimizeShopifyUrl(hoverImgBase, '240')} 240w, 
        ${this.optimizeShopifyUrl(hoverImgBase, '360')} 360w, 
        ${this.optimizeShopifyUrl(hoverImgBase, '480')} 480w, 
        ${this.optimizeShopifyUrl(hoverImgBase, '640')} 640w
      `.trim();
      
      // Badges Logic
      let badgesHTML = '';
      if (isOnSale || isSoldOut) {
        badgesHTML = `<div class="product-card-badges">`;
        if (isOnSale) badgesHTML += `<span class="product-badges__badge product-badges__badge--rectangle sale-badge ${cardSize}">${this.i18n.sale}</span>`;
        if (isSoldOut) badgesHTML += `<span class="product-badges__badge product-badges__badge--rectangle sold-badge ${cardSize}">${this.i18n.sold_out}</span>`;
        badgesHTML += `</div>`;
      }

      const splitTitle = p.title.split(' | ');
      const titleMain = splitTitle.length > 1 ? splitTitle[0].trim() : p.title;
      const titleSub = splitTitle.length > 1 ? splitTitle[1].trim() : '';

      const formattedPrice = this.formatMoney(p.price);
      let priceHTML = formattedPrice;
      if (p.price_varies && this.i18n.from_html) {
        priceHTML = this.i18n.from_html.replace('[price]', formattedPrice);
      }
      const compareHTML = isOnSale ? `<span class="compare-at-price ${cardSize}">${this.formatMoney(p.compare_at_price)}</span>` : '';

      const hoverAnimation = this.dataset.hoverAnimation;
      const hoverImageEnabled = this.dataset.hoverImage === 'true' && !isMobile;

      return `
        <div class="product-card-wrapper" data-hover="${hoverAnimation}">
          <a href="${p.url}" class="product-card-link">
            <div class="product-card-image-wrapper">
            ${badgesHTML}
            <img 
                src="${mainImg}" 
                srcset="${mainImgSrcset}"
                sizes="(max-width: 749px) 44vw, (min-width: 750px) calc((1200px - 60px) / ${this.dataset.columns || 4})"
                class="product-card-image primary ${hoverImageEnabled ? 'has-hover' : ''}" 
                loading="lazy" 
                width="400" height="400"
                alt="${p.title}"
            >
            ${hoverImageEnabled ? `
                <img 
                src="${hoverImg}" 
                srcset="${hoverImgSrcset}"
                sizes="(max-width: 749px) 44vw, (min-width: 750px) calc((1200px - 60px) / ${this.dataset.columns || 4})"
                class="product-card-image secondary has-hover" 
                loading="lazy"
                width="400" height="400"
                alt="${p.title}"
                >` : ''}
            </div>
            <div class="product-card-info ${cardSize}">
              <div class="product-card-collection-title">${p.collection}</div>
              <div>
                <h3 class="product-title-card ${cardSize}">${titleMain}</h3>
                ${titleSub ? `<p class="product-subtitle-card ${cardSize}">${titleSub}</p>` : ''}
              </div>
              <div class="product-card-price ${cardSize}">
                <div class="product-price">  
                  <span class="price">${priceHTML}</span>
                  ${compareHTML}
                </div>
              </div>
            </div>
          </a>
        </div>
      `;
    }

    formatMoney(cents) {
      const value = cents / 100;
      let moneyFormat = this.moneyFormat.toLowerCase()
      let currencySymbol = this.currencySymbol.toLowerCase()
      let formatted = moneyFormat.replace(currencySymbol, '<small>' + currencySymbol + '</small>').replace(' ', '');
      if (moneyFormat.includes('{{amount_no_decimals}}') ) {
        return formatted.replace('{{amount_no_decimals}}', Math.round(value).toString());
      } else if (moneyFormat.includes('{{amount_with_comma_separator}}') ) {
        return formatted.replace('{{amount_with_comma_separator}}', parseFloat(value.toFixed(2).replace('.', ',')));
      } else if (moneyFormat.includes('{{amount_no_trailing_zeros}}') ) {
        return formatted.replace('{{amount_no_trailing_zeros}}', parseFloat(value.toFixed(2).replace('.', ',')));
      } else {
        return 'N/A';
      }
    }

    initNav() {
      if (this.prevBtn && this.nextBtn) {
        this.prevBtn.style.display = 'flex';
        this.nextBtn.style.display = 'flex';
        this.prevBtn.onclick = () => this.carousel.scrollBy({ left: -this.carousel.clientWidth / 2, behavior: 'smooth' });
        this.nextBtn.onclick = () => this.carousel.scrollBy({ left: this.carousel.clientWidth / 2, behavior: 'smooth' });
      }
    }
  }
  
  customElements.define('smart-carousel', SmartCarousel);
}